import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface Plan {
  id: string;
  name: string;
  price_monthly: number;
  price_yearly: number;
  max_bases: number;
  max_automations: number;
  max_ai_calls_daily: number;
  max_storage_mb: number;
  max_records_per_table: number;
  has_forms: boolean;
  has_advanced_views: boolean;
  has_agent: boolean;
  has_sso: boolean;
  has_api_access: boolean;
  has_priority_support: boolean;
}

interface Subscription {
  id: string;
  workspace_id: string;
  plan_id: string;
  status: "active" | "canceled" | "past_due" | "trialing";
  trial_ends_at: string | null;
  current_period_end: string;
  plan: Plan;
}

interface UsageCheck {
  allowed: boolean;
  current: number;
  limit: number;
  remaining: number;
}

type UsageType = "ai_call" | "automation_run" | "storage_upload" | "record_create" | "form_submit";

export function useSubscription(workspaceId?: string) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<Plan[]>([]);

  // Fetch subscription for workspace
  useEffect(() => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    const fetchSubscription = async () => {
      try {
        const { data, error } = await supabase
          .from("workspace_subscriptions")
          .select(`
            *,
            plan:plans(*)
          `)
          .eq("workspace_id", workspaceId)
          .single();

        if (error && error.code !== "PGRST116") {
          console.error("Failed to fetch subscription:", error);
        }

        if (data) {
          setSubscription(data as unknown as Subscription);
        } else {
          // Create default free subscription if none exists
          const { data: newSub, error: createError } = await supabase
            .from("workspace_subscriptions")
            .insert({ workspace_id: workspaceId, plan_id: "free" })
            .select(`*, plan:plans(*)`)
            .single();

          if (!createError && newSub) {
            setSubscription(newSub as unknown as Subscription);
          }
        }
      } catch (err) {
        console.error("Subscription fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [workspaceId]);

  // Fetch all available plans
  useEffect(() => {
    const fetchPlans = async () => {
      const { data } = await supabase
        .from("plans")
        .select("*")
        .order("price_monthly", { ascending: true });

      if (data) {
        setPlans(data as Plan[]);
      }
    };

    fetchPlans();
  }, []);

  // Check if a feature is available
  const hasFeature = useCallback(
    (feature: keyof Pick<Plan, "has_forms" | "has_advanced_views" | "has_agent" | "has_sso" | "has_api_access" | "has_priority_support">) => {
      if (!subscription?.plan) return false;
      return subscription.plan[feature];
    },
    [subscription]
  );

  // Get limit value (-1 = unlimited)
  const getLimit = useCallback(
    (limitKey: keyof Pick<Plan, "max_bases" | "max_automations" | "max_ai_calls_daily" | "max_storage_mb" | "max_records_per_table">) => {
      if (!subscription?.plan) return 0;
      return subscription.plan[limitKey];
    },
    [subscription]
  );

  // Check usage limit via RPC
  const checkUsage = useCallback(
    async (usageType: UsageType): Promise<UsageCheck> => {
      if (!workspaceId) {
        return { allowed: false, current: 0, limit: 0, remaining: 0 };
      }

      const limitColumn = {
        ai_call: "max_ai_calls_daily",
        automation_run: "max_automations",
        storage_upload: "max_storage_mb",
        record_create: "max_records_per_table",
        form_submit: "max_records_per_table",
      }[usageType];

      try {
        const { data, error } = await supabase.rpc("check_usage_limit", {
          _workspace_id: workspaceId,
          _usage_type: usageType,
          _limit_column: limitColumn,
        });

        if (error) {
          console.error("Usage check error:", error);
          return { allowed: false, current: 0, limit: 0, remaining: 0 };
        }

        // Parse the JSONB response from Supabase RPC
        const result = data as unknown as UsageCheck;
        return result || { allowed: false, current: 0, limit: 0, remaining: 0 };
      } catch (err) {
        console.error("Usage check error:", err);
        return { allowed: false, current: 0, limit: 0, remaining: 0 };
      }
    },
    [workspaceId]
  );

  // Increment usage counter
  const trackUsage = useCallback(
    async (usageType: UsageType, count = 1): Promise<boolean> => {
      if (!workspaceId || !user?.id) return false;

      try {
        const { error } = await supabase.rpc("increment_usage", {
          _workspace_id: workspaceId,
          _user_id: user.id,
          _usage_type: usageType,
          _count: count,
        });

        return !error;
      } catch {
        return false;
      }
    },
    [workspaceId, user?.id]
  );

  // Gate a feature with toast notification
  const gateFeature = useCallback(
    (feature: keyof Pick<Plan, "has_forms" | "has_advanced_views" | "has_agent" | "has_sso">): boolean => {
      if (hasFeature(feature)) return true;

      const featureNames: Record<string, string> = {
        has_forms: "Forms",
        has_advanced_views: "Advanced Views",
        has_agent: "AI Agent",
        has_sso: "SSO",
      };

      toast.error(`${featureNames[feature]} requires an upgrade`, {
        description: "Upgrade to Starter or Pro to unlock this feature.",
        action: {
          label: "Upgrade",
          onClick: () => window.location.href = "/settings?tab=billing",
        },
      });

      return false;
    },
    [hasFeature]
  );

  // Gate usage with limit check
  const gateUsage = useCallback(
    async (usageType: UsageType): Promise<boolean> => {
      const check = await checkUsage(usageType);

      if (check.allowed) return true;

      const usageNames: Record<UsageType, string> = {
        ai_call: "AI calls",
        automation_run: "automations",
        storage_upload: "storage",
        record_create: "records",
        form_submit: "form submissions",
      };

      toast.error(`${usageNames[usageType]} limit reached`, {
        description: `You've used ${check.current}/${check.limit}. Upgrade for more.`,
        action: {
          label: "Upgrade",
          onClick: () => window.location.href = "/settings?tab=billing",
        },
      });

      return false;
    },
    [checkUsage]
  );

  // Check if on trial
  const isTrialing = subscription?.status === "trialing" && 
    subscription.trial_ends_at && 
    new Date(subscription.trial_ends_at) > new Date();

  // Check if subscription is active (including trial)
  const isActive = subscription?.status === "active" || isTrialing;

  // Days remaining in trial
  const trialDaysRemaining = isTrialing && subscription?.trial_ends_at
    ? Math.ceil((new Date(subscription.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : 0;

  return {
    subscription,
    plan: subscription?.plan || null,
    plans,
    loading,
    isActive,
    isTrialing,
    trialDaysRemaining,
    hasFeature,
    getLimit,
    checkUsage,
    trackUsage,
    gateFeature,
    gateUsage,
  };
}
